import {mesh, feature} from 'topojson-client';

import usa from './topo.usa.min.json';
import citiesRaw from './cities.min.json';

import _ from 'lodash';

class TopoUtils {

  constructor() {
    this.cache = {};
  }

  /**
   * Returns a topojson model for the usa including
   *
   * land : topojson feature of the us outline
   * borders : topojson mesh of state borders
   * states : topojson feature collection of all us states
   * counties : topojson feature collection of all us counties
   *
   * @returns {{land: *, borders: *, states: (features)}}
   */
  getNationalTopography() {
    const key = `national`;

    if (!this.cache[key]) {
      const land = feature(usa, usa.objects.nation);
      const states = feature(usa, usa.objects.states).features;
      const counties = feature(usa, usa.objects.counties).features;
      const borders = mesh(usa, usa.objects.states, function (a, b) {
        return a !== b;
      });

      this.cache[key] = {land, borders, states, counties};
    }

    return this.cache[key] || {};
  }

  /**
   * Returns a topojson model for a state relating to the supplied fip
   * uses an internal cache to speed up subsequent requests for the state topo
   *
   * land : topojson feature of the state outline
   * borders : topojson mesh of states internal county borders
   * states : topojson feature collection of all counties in state
   *
   * @param fips - integer fips code for state desired
   * @returns {{land: *, borders: *, counties: (features)}}
   */
  getStateTopography(fips) {
    const key = `s-${fips}`;

    if (fips && !this.cache[key]) {
      const countyTopo = this.getCountiesGeometry(fips);
      const stateTopo = this.getStateGeometry(fips);
      const cities = _.filter(citiesRaw, ['sf', fips]);
      const land = feature(usa, stateTopo);
      const counties = feature(usa, countyTopo).features;
      const borders = mesh(usa, countyTopo, function (a, b) {
        return a !== b;
      });

      this.cache[key] = {land, borders, counties, cities};
    }

    return this.cache[key] || {};
  }

  /**
   * Returns a filtered GeometryCollection containing only the counties
   * contained withing the state specified by stateFips.
   * If no stateFips provided, all counties are returned.
   *
   * @param stateFips - integer fips code for state desired
   * @returns {{type: *, geometries}}
   */
  getCountiesGeometry(stateFips) {
    return {
      type: "GeometryCollection",
      geometries: _.filter(usa.objects.counties.geometries, county => {
        return !stateFips || stateFips === Math.floor(county.id / 1000);
      })
    };
  }

  /**
   * Returns a filtered GeometryCollection containing only
   * the state shape specified by stateFips.
   * If no stateFips provided, all us state geometries are returned.
   *
   * @param stateFips - integer fips code for state desired
   * @returns {{type: *, geometries}}
   */
  getStateGeometry(stateFips) {
    return {
      type: "GeometryCollection",
      geometries: _.filter(usa.objects.states.geometries, state => {
        return !stateFips || stateFips === state.id;
      })
    };
  }

}

export let topoUtils = new TopoUtils();
